import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { AutoAwesomeRounded as AutoAwesomeIcon } from '@mui/icons-material';
import colors from '../../assets/theme/colors';
import useAskRecord from '../../hooks/askRecord/useAskRecord';
import AskRecordConversationResult from './AskRecordConversationResult';
import AskRecordInlineResult from './AskRecordInlineResult';
import AskRecordInputForm from './AskRecordInputForm';
import AskRecordSuggestedQuestions from './AskRecordSuggestedQuestions';
import AskRecordThinkingState from './AskRecordThinkingState';
import {
  ASK_ACCENT,
  ASK_ACCENT_DARK,
  ASK_SAGE_TINT,
  ASK_TINT,
  getFirstName,
} from './askRecordUi';

const AskRecordDecorations = ({ accentPrimary, compact }) => (
  <>
    <Box
      aria-hidden="true"
      sx={{
        position: 'absolute',
        top: -34,
        right: -28,
        width: compact ? 96 : 118,
        height: compact ? 96 : 118,
        borderRadius: '50%',
        bgcolor: alpha(accentPrimary, 0.08),
        border: `1px solid ${alpha(accentPrimary, 0.08)}`,
        pointerEvents: 'none',
      }}
    />
    <Box
      aria-hidden="true"
      sx={{
        position: 'absolute',
        bottom: -42,
        left: -34,
        width: compact ? 82 : 104,
        height: compact ? 82 : 104,
        borderRadius: '50%',
        bgcolor: alpha(accentPrimary, 0.06),
        border: `1px solid ${alpha(accentPrimary, 0.06)}`,
        pointerEvents: 'none',
      }}
    />
  </>
);

const AskRecordHeader = ({ accentInk, accentPrimary, compact, firstName }) => (
  <Box sx={{ position: 'relative', zIndex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.9 }}>
    <Box
      sx={{
        width: compact ? 34 : 38,
        height: compact ? 34 : 38,
        borderRadius: '999px',
        display: 'grid',
        placeItems: 'center',
        bgcolor: alpha(accentPrimary, 0.12),
        color: accentInk,
        border: `1px solid ${alpha(accentPrimary, 0.12)}`,
        boxShadow: `0 8px 18px ${alpha(accentPrimary, 0.1)}`,
        flexShrink: 0,
      }}
    >
      <AutoAwesomeIcon sx={{ fontSize: compact ? 18 : 20 }} />
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: compact ? '0.88rem' : '0.96rem', fontWeight: 820, color: colors.semantic.ink, lineHeight: 1.08 }}>
        Ask about {firstName}&apos;s care
      </Typography>
      <Typography sx={{ mt: 0.22, fontSize: compact ? '0.7rem' : '0.74rem', color: alpha(colors.semantic.ink, 0.62), lineHeight: 1.15, fontWeight: 600 }}>
        Search saved care notes
      </Typography>
    </Box>
  </Box>
);

const AskRecordPanel = ({
  accent,
  child,
  compact = false,
  showDecorations = true,
  showHeader = true,
  inputPlaceholder = 'Depakote level, sleep changes...',
  resultLayout = 'inline',
  formSx,
  inputSx,
  onOpenSource,
  onResultStateChange,
  sx,
}) => {
  const [question, setQuestion] = useState('');
  const [lastQuestion, setLastQuestion] = useState('');
  const {
    answer,
    ask,
    confidence,
    conversation,
    createSummary,
    error,
    followUps,
    loading,
    sources,
    summary,
    summaryError,
    summaryLoading,
  } = useAskRecord({ childId: child?.id });
  const firstName = useMemo(() => getFirstName(child?.name), [child?.name]);
  const canAsk = question.trim().length > 0 && !loading && Boolean(child?.id);
  const useConversationResult = resultLayout === 'conversation' && Boolean(answer);
  const showInlineThinking = loading && !useConversationResult;
  const activeAccent = accent || {
    primary: ASK_ACCENT,
    ink: ASK_ACCENT_DARK,
    soft: ASK_TINT,
    wash: ASK_SAGE_TINT,
  };
  const accentPrimary = activeAccent.primary || ASK_ACCENT;
  const accentInk = activeAccent.ink || ASK_ACCENT_DARK;
  const accentSoft = activeAccent.soft || alpha(accentPrimary, 0.08);
  const accentWash = activeAccent.wash || alpha(accentPrimary, 0.025);

  const handleAsk = async (nextQuestion = question, options = {}) => {
    const trimmedQuestion = String(nextQuestion || '').trim();
    if (!trimmedQuestion || loading) {
      return;
    }
    const displayQuestion = String(options.displayQuestion || trimmedQuestion).trim();
    setLastQuestion(displayQuestion);
    try {
      await ask(trimmedQuestion);
      setQuestion('');
    } catch (_error) {
      // The hook owns the visible error state.
    }
  };

  useEffect(() => {
    if (!onResultStateChange) {
      return;
    }
    onResultStateChange({
      hasResult: useConversationResult,
      question: lastQuestion,
    });
  }, [lastQuestion, onResultStateChange, useConversationResult]);

  useEffect(() => {
    setLastQuestion('');
  }, [child?.id]);

  const handleTypedFollowUp = () => {
    const followUpQuestion = String(question || '').trim();
    return handleAsk(followUpQuestion);
  };

  const renderAskForm = ({ isFollowUp = false } = {}) => (
    <AskRecordInputForm
      accent={activeAccent}
      canAsk={canAsk}
      compact={compact}
      firstName={firstName}
      formSx={formSx}
      inputPlaceholder={inputPlaceholder}
      inputSx={inputSx}
      isFollowUp={isFollowUp}
      loading={loading}
      onAsk={isFollowUp ? handleTypedFollowUp : handleAsk}
      question={question}
      setQuestion={setQuestion}
    />
  );

  return (
    <Paper
      elevation={0}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        p: compact ? 1.05 : 1.2,
        borderRadius: compact ? '20px' : '22px',
        bgcolor: '#FFFFFF',
        background: `linear-gradient(135deg, #FFFFFF 0%, ${alpha(accentWash, 0.92)} 58%, ${alpha(accentSoft, 0.7)} 100%)`,
        border: `1px solid ${alpha(accentPrimary, 0.18)}`,
        boxShadow: `0 14px 34px ${alpha(accentPrimary, 0.06)}`,
        ...sx,
      }}
    >
      {showDecorations ? <AskRecordDecorations accentPrimary={accentPrimary} compact={compact} /> : null}
      {showHeader ? <AskRecordHeader accentInk={accentInk} accentPrimary={accentPrimary} compact={compact} firstName={firstName} /> : null}

      {useConversationResult ? null : renderAskForm()}

      {showInlineThinking ? (
        <AskRecordThinkingState compact={compact} firstName={firstName} />
      ) : null}

      {!loading && !answer && !error ? (
        <AskRecordSuggestedQuestions child={child} firstName={firstName} loading={loading} onAsk={handleAsk} />
      ) : null}

      {error ? (
        <Typography sx={{ position: 'relative', zIndex: 1, mt: 0.8, fontSize: '0.78rem', lineHeight: 1.35, color: colors.semantic.error, fontWeight: 750 }}>
          {error}
        </Typography>
      ) : null}

      {answer && useConversationResult ? (
        <AskRecordConversationResult
          conversation={conversation}
          firstName={firstName}
          confidence={confidence}
          lastQuestion={lastQuestion}
          loading={loading}
          onAsk={handleAsk}
          onCreateSummary={createSummary}
          onOpenSource={onOpenSource}
          renderFollowUpForm={() => renderAskForm({ isFollowUp: true })}
          followUps={followUps}
          sources={sources}
          summary={summary}
          summaryError={summaryError}
          summaryLoading={summaryLoading}
        />
      ) : null}

      {answer && !useConversationResult && !loading ? (
        <AskRecordInlineResult
          answer={answer}
          compact={compact}
          confidence={confidence}
          onOpenSource={onOpenSource}
          sources={sources}
        />
      ) : null}
    </Paper>
  );
};

export default AskRecordPanel;
